import {
  isConnectorRouteAuth,
  isRouteType,
  type RouteType,
} from "../../../shared/stepConfig";
import type { RouteAuthMode } from "../../lib/routeAuthDisplay";
import { ROUTE_AUTH_MODES } from "../../lib/routeAuthDisplay";

type MonitorRouteBranchInput = {
  name: string;
  headCommit?: {
    routes?: ReadonlyArray<{
      route: string;
      stepDir: string;
      routeType: string | null;
      routeAuth: string | null;
    }>;
  } | null;
};

export type MonitorRouteWorkflowInput = {
  id: string;
  name: string;
  spaceId: string;
  branches?: MonitorRouteBranchInput | ReadonlyArray<MonitorRouteBranchInput>;
};

function mainBranch(workflow: MonitorRouteWorkflowInput) {
  const branches = workflow.branches;
  if (!branches) return null;
  const list = Array.isArray(branches) ? branches : [branches];
  return list.find((b) => b.name === "main");
}

export type MonitorRouteRow = {
  workflowId: string;
  workflowName: string;
  spaceId: string;
  route: string;
  stepDir: string;
  routeType: RouteType | null;
  routeAuth: RouteAuthMode;
};

export function normalizeRouteAuth(
  raw: string | null | undefined
): RouteAuthMode {
  if (raw === "public") return "public";
  if (raw === "external_id") return "external_id";
  if (raw === "tenant") return "tenant";
  if (raw === "sso") return "sso";
  if (isConnectorRouteAuth(raw ?? undefined)) return "connected_app";
  return "space";
}

export function computeMonitorRoutes(
  workflows: ReadonlyArray<MonitorRouteWorkflowInput>
): MonitorRouteRow[] {
  const rows: MonitorRouteRow[] = [];

  for (const workflow of workflows) {
    const head = mainBranch(workflow)?.headCommit;
    if (!head?.routes?.length) continue;

    for (const route of head.routes) {
      rows.push({
        workflowId: workflow.id,
        workflowName: workflow.name,
        spaceId: workflow.spaceId,
        route: route.route,
        stepDir: route.stepDir,
        routeType: isRouteType(route.routeType) ? route.routeType : null,
        routeAuth: normalizeRouteAuth(route.routeAuth),
      });
    }
  }

  return rows.sort(
    (a, b) =>
      ROUTE_AUTH_MODES.indexOf(a.routeAuth) -
        ROUTE_AUTH_MODES.indexOf(b.routeAuth) ||
      a.route.localeCompare(b.route) ||
      a.workflowName.localeCompare(b.workflowName)
  );
}

export type MonitorWorkflowRouteSummary = {
  workflowId: string;
  workflowName: string;
  spaceId: string;
  routes: MonitorRouteRow[];
};

export function groupRoutesByWorkflow(
  rows: ReadonlyArray<MonitorRouteRow>
): MonitorWorkflowRouteSummary[] {
  const byId = new Map<string, MonitorWorkflowRouteSummary>();
  for (const row of rows) {
    const existing = byId.get(row.workflowId);
    if (existing) {
      existing.routes.push(row);
      continue;
    }
    byId.set(row.workflowId, {
      workflowId: row.workflowId,
      workflowName: row.workflowName,
      spaceId: row.spaceId,
      routes: [row],
    });
  }
  return Array.from(byId.values()).sort((a, b) =>
    a.workflowName.localeCompare(b.workflowName)
  );
}

export function countWorkflowsByRouteAuth(
  summaries: ReadonlyArray<MonitorWorkflowRouteSummary>
): Record<RouteAuthMode, number> {
  const counts = Object.fromEntries(
    ROUTE_AUTH_MODES.map((mode) => [mode, 0])
  ) as Record<RouteAuthMode, number>;
  for (const summary of summaries) {
    const auths = new Set(summary.routes.map((route) => route.routeAuth));
    for (const mode of ROUTE_AUTH_MODES) {
      if (auths.has(mode)) counts[mode] += 1;
    }
  }
  return counts;
}

export function matchingRoutes(
  summary: MonitorWorkflowRouteSummary,
  authFilter: RouteAuthMode | null
): MonitorRouteRow[] {
  if (!authFilter) return [...summary.routes];
  return summary.routes.filter((row) => row.routeAuth === authFilter);
}

export function filterWorkflowSummaries(
  summaries: ReadonlyArray<MonitorWorkflowRouteSummary>,
  authFilter: RouteAuthMode | null
): MonitorWorkflowRouteSummary[] {
  return summaries.filter(
    (summary) => matchingRoutes(summary, authFilter).length > 0
  );
}

export type WorkflowRouteSort = "name" | "routes";

export function sortWorkflowRouteSummaries(
  summaries: ReadonlyArray<MonitorWorkflowRouteSummary>,
  sort: WorkflowRouteSort,
  authFilter: RouteAuthMode | null
): MonitorWorkflowRouteSummary[] {
  const list = [...summaries];
  list.sort((a, b) => {
    if (sort === "routes") {
      const aCount = matchingRoutes(a, authFilter).length;
      const bCount = matchingRoutes(b, authFilter).length;
      return bCount - aCount || a.workflowName.localeCompare(b.workflowName);
    }
    return a.workflowName.localeCompare(b.workflowName);
  });
  return list;
}
